import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { CustomButton, CustomCheckbox } from "../../../../v2/MUI_custom_component";
import FilePreviewCard from "../../../../v2/Utils/FileUpload/FilePreviewCard";
import { toCapitalized } from "../../../../v2/Utils/Capitalize";
import {
  validateFileType,
  validateFileSize,
} from "../../../Common/OCR/utils/validation";
import {
  ReusableOCRFailureModal,
  ReusableManualEntryForm,
} from "../../../Common/OCR";
import { AdhaarOCRVerficiationAction } from "../../../Beneficiary/StaffInsurance/APIs/action";
import { CONSENT_TEXT } from "../../utils/consentText";
import AadhaarUploadBox from "./AadhaarUploadBox";

dayjs.extend(customParseFormat);

const ACCEPTED = [".jpg", ".jpeg", ".png", ".pdf"];
const MAX_SIZE = 5 * 1024 * 1024; 

const AdharOCR = ({ onSave }) => {
  const selectedRole = useSelector(
    (state) => state.onboardBuyerReducer.selectedRole,
  );
  const roleName = selectedRole?.display_name
    ? toCapitalized(selectedRole.display_name)
    : "";

  const [step, setStep] = useState("upload");
  const [extractedData, setExtractedData] = useState(null);
  const [frontFile, setFrontFile] = useState(null);
  const [backFile, setBackFile] = useState(null);
  const [frontError, setFrontError] = useState("");
  const [backError, setBackError] = useState("");
  const [consent, setConsent] = useState(false);
  const [consentError, setConsentError] = useState("");
  const [reviewConsent, setReviewConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showFailureModal, setShowFailureModal] = useState(false);
  const [ocrError, setOcrError] = useState("");

  const frontPreview = useMemo(
    () =>
      frontFile && frontFile.type !== "application/pdf"
        ? URL.createObjectURL(frontFile)
        : null,
    [frontFile],
  );
  const backPreview = useMemo(
    () =>
      backFile && backFile.type !== "application/pdf"
        ? URL.createObjectURL(backFile)
        : null,
    [backFile],
  );
  useEffect(
    () => () => frontPreview && URL.revokeObjectURL(frontPreview),
    [frontPreview],
  );
  useEffect(
    () => () => backPreview && URL.revokeObjectURL(backPreview),
    [backPreview],
  );

  const validateFile = (file) => {
    const type = validateFileType(file, ACCEPTED);
    if (!type.isValid) return type.error;
    const size = validateFileSize(file, MAX_SIZE);
    if (!size.isValid) return size.error;
    return null;
  };

  const handleFrontSelect = (file) => {
    const err = validateFile(file);
    if (err) return setFrontError(err);
    setFrontError("");
    setFrontFile(file);
  };

  const handleBackSelect = (file) => {
    const err = validateFile(file);
    if (err) return setBackError(err);
    setBackError("");
    setBackFile(file);
  };

  const handleSave = async () => {
    if (!frontFile) return;
    if (!consent) {
      setConsentError("Please accept the consent to proceed");
      return;
    }

    const fd = new FormData();
    fd.append("file_front", frontFile);
    if (backFile) fd.append("file_back", backFile);
    fd.append("consent", "Y");
    fd.append("require_internal_address_mapping", "true");

    setLoading(true);
    const res = await AdhaarOCRVerficiationAction(fd);
    setLoading(false);

    if (res?.error || !res?.data) {
      setOcrError(res?.message || "Couldn't read the document");
      setShowFailureModal(true);
      return;
    }
    setExtractedData(res.data);
    setStep("review");
  };

  const heading = roleName ? `${roleName} Details` : "User Details";

  const formatDob = (dob) => {
    if (!dob) return "-";
    const d = dayjs(dob, ["YYYY-MM-DD", "DD-MM-YYYY"]);
    return d.isValid() ? d.format("DD MMMM YYYY") : "-";
  };

  const removeFront = () => {
    setFrontFile(null);
    setStep("upload");
  };
  const removeBack = () => {
    setBackFile(null);
    setStep("upload");
  };

  return (
    <Box
      sx={{
        width: step === "manual" ? "864px" : "616px",
        maxWidth: "100%",
      }}>
      <ReusableOCRFailureModal
        open={showFailureModal}
        onClose={() => {
          setShowFailureModal(false);
          setOcrError("");
        }}
        onTryAgain={() => {
          setShowFailureModal(false);
          setOcrError("");
        }}
        onEnterManually={() => {
          setShowFailureModal(false);
          setStep("manual");
        }}
        errorMessage={ocrError}
      />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: { xs: 4, md: 8 },
        }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: { xs: 4, md: 2 },
          }}>
          <Typography variant="h2" sx={{ color: "#333333" }}>
            {heading}
          </Typography>

          {step === "manual" ? (
            <ReusableManualEntryForm
              requirePhone={false}
              requireEmail={false}
              requireRelation={false}
              showAge={false}
              saveButtonText="Continue"
              showCancelButton={false}
              showConfirmation={false}
              gridContainerSx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "repeat(3, minmax(0, 1fr))",
                },
                gap: 1,
              }}
              actionsSx={{
                mt: { xs: 4, md: 8 },
                display: "flex",
                gap: 2,
                justifyContent: "flex-end",
              }}
              onCancel={() => setStep("upload")}
              onSave={(formData) => onSave?.(formData, "MANUAL")}
            />
          ) : step === "review" ? (
            <>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Typography
                  variant="labelM"
                  sx={{
                    color: "#666666",
                  }}>
                  Uploaded Aadhar
                </Typography>
                <FilePreviewCard
                  uploadedFile={frontFile}
                  filePreview={frontPreview}
                  onDelete={removeFront}
                />
                {backFile && (
                  <FilePreviewCard
                    uploadedFile={backFile}
                    filePreview={backPreview}
                    onDelete={removeBack}
                  />
                )}
              </Box>

              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  flexWrap: "wrap",
                  width: "100%",
                }}>
                {[
                  {
                    label: "Name",
                    value:
                      toCapitalized(
                        extractedData?.name || extractedData?.full_name,
                      ) || "-",
                  },
                  {
                    label: "Gender",
                    value: toCapitalized(extractedData?.gender || "-"),
                  },
                  {
                    label: "Date of Birth",
                    value: formatDob(
                      extractedData?.date_of_birth || extractedData?.dob,
                    ),
                  },
                ].map((f) => (
                  <Box key={f.label} sx={{ minWidth: 132, flex: "1 0 0" }}>
                    <Typography
                      variant="labelM"
                      sx={{ color: "#666666", display: "block" }}>
                      {f.label}
                    </Typography>
                    <Typography variant="p1" sx={{ color: "#333333" }}>
                      {f.value}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <CustomCheckbox
                checked={reviewConsent}
                onChange={(e) => setReviewConsent(e.target.checked)}
                label={CONSENT_TEXT}
                alignment="flex-start"
              />
            </>
          ) : (
            <>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 1,
                  width: "100%",
                }}>
                <AadhaarUploadBox
                  label={`${frontFile ? "Uploaded" : "Upload"} front side of AADHAAR (Mandatory)`}
                  file={frontFile}
                  onSelect={handleFrontSelect}
                  onDelete={() => {
                    setFrontFile(null);
                    setFrontError("");
                  }}
                  error={frontError}
                />
                <AadhaarUploadBox
                  label="Upload back side of AADHAAR (optional)"
                  file={backFile}
                  onSelect={handleBackSelect}
                  onDelete={() => {
                    setBackFile(null);
                    setBackError("");
                  }}
                  error={backError}
                />
              </Box>

              <Box>
                <CustomCheckbox
                  checked={consent}
                  onChange={(e) => {
                    setConsent(e.target.checked);
                    if (e.target.checked) setConsentError("");
                  }}
                  label={CONSENT_TEXT}
                />
                {consentError && (
                  <Typography
                    sx={{ fontSize: "13px", color: "#CC0000", mt: 0.5 }}>
                    {consentError}
                  </Typography>
                )}
              </Box>
            </>
          )}
        </Box>
        {step === "upload" && (
          <CustomButton
            label="Save"
            fullWidth
            disabled={!frontFile || !consent || loading}
            loading={loading}
            onClick={handleSave}
          />
        )}
        {step === "review" && (
          <CustomButton
            label="Continue"
            fullWidth
            disabled={!reviewConsent}
            onClick={() => onSave?.(extractedData, "OCR")}
          />
        )}
      </Box>
    </Box>
  );
};

export default AdharOCR;
